import { allGenres } from '@/mocks/movies';

export interface MovieFilters {
  genres: string[];
  yearMin: number;
  yearMax: number;
  ratingMin: number;
  sort: 'default' | 'rating' | 'year';
}

export const defaultFilters: MovieFilters = {
  genres: [],
  yearMin: 2020,
  yearMax: 2024,
  ratingMin: 0,
  sort: 'default',
};

const YEARS = [2020, 2021, 2022, 2023, 2024];
const RATINGS = [0, 7, 7.5, 8, 8.5, 9];

interface FilterBarProps {
  filters: MovieFilters;
  onChange: (next: MovieFilters) => void;
  onReset: () => void;
  onOpenMobile: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}

function FilterPanel({
  filters,
  onChange,
  onReset,
}: Pick<FilterBarProps, 'filters' | 'onChange' | 'onReset'>) {
  const toggleGenre = (g: string) => {
    const has = filters.genres.includes(g);
    onChange({
      ...filters,
      genres: has ? filters.genres.filter((x) => x !== g) : [...filters.genres, g],
    });
  };

  const selectClass =
    'h-10 px-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer';

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-foreground-900">类型（可多选）</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {allGenres.map((g) => {
            const active = filters.genres.includes(g);
            return (
              <button
                key={g}
                type="button"
                onClick={() => toggleGenre(g)}
                className={`px-3 py-1.5 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  active
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 border border-background-200 hover:border-primary-300'
                }`}
              >
                {g}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground-900">上映年份</h3>
          <div className="mt-3 flex items-center gap-2">
            <select
              value={filters.yearMin}
              onChange={(e) => {
                const v = Number(e.target.value);
                onChange({ ...filters, yearMin: v, yearMax: Math.max(v, filters.yearMax) });
              }}
              className={`${selectClass} flex-1 min-w-0`}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y} 年
                </option>
              ))}
            </select>
            <span className="text-foreground-400 text-sm shrink-0">至</span>
            <select
              value={filters.yearMax}
              onChange={(e) => {
                const v = Number(e.target.value);
                onChange({ ...filters, yearMax: v, yearMin: Math.min(v, filters.yearMin) });
              }}
              className={`${selectClass} flex-1 min-w-0`}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y} 年
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold text-foreground-900">最低评分</h3>
          <div className="mt-3">
            <select
              value={filters.ratingMin}
              onChange={(e) => onChange({ ...filters, ratingMin: Number(e.target.value) })}
              className={`${selectClass} w-full`}
            >
              {RATINGS.map((r) => (
                <option key={r} value={r}>
                  {r === 0 ? '不限评分' : `${r.toFixed(1)} 分以上`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <p className="text-xs text-foreground-400">筛选条件会自动刷新下方列表</p>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
          重置筛选
        </button>
      </div>
    </div>
  );
}

export default function FilterBar({
  filters,
  onChange,
  onReset,
  onOpenMobile,
  mobileOpen,
  onCloseMobile,
}: FilterBarProps) {
  return (
    <>
      {/* 桌面端 */}
      <div className="hidden lg:block p-6 rounded-lg bg-background-50 border border-background-200">
        <FilterPanel filters={filters} onChange={onChange} onReset={onReset} />
      </div>

      {/* 移动端按钮 */}
      <div className="lg:hidden">
        <button
          type="button"
          onClick={onOpenMobile}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm font-medium text-foreground-800 cursor-pointer"
        >
          <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center"></i>
          筛选与排序
          {filters.genres.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-primary-950 text-xs">
              {filters.genres.length}
            </span>
          )}
        </button>

        {mobileOpen && (
          <div className="fixed inset-0 z-50 flex items-end">
            <div
              className="absolute inset-0 bg-black/50"
              onClick={onCloseMobile}
              aria-hidden="true"
            ></div>
            <div className="relative w-full max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background-50 p-5 pb-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-base font-semibold text-foreground-950">筛选与排序</h2>
                <button
                  type="button"
                  onClick={onCloseMobile}
                  aria-label="关闭筛选"
                  className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
              <FilterPanel filters={filters} onChange={onChange} onReset={onReset} />
              <button
                type="button"
                onClick={onCloseMobile}
                className="mt-6 w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}